import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import '../styles/DescriptionEditor.css';

interface DescriptionEditorProps {
  token: string;
  apiBase: string;
}

const DescriptionEditor: React.FC<DescriptionEditorProps> = ({ token, apiBase }) => {
  const [description, setDescription] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const fetchDescription = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/description`);
      if (response.ok) {
        const data = await response.json();
        setDescription(data.description);
        setOriginalDescription(data.description);
      }
    } catch (err) {
      setError('Failed to load description');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchDescription();
  }, [fetchDescription]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${apiBase}/description`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ description })
      });

      if (response.ok) {
        setOriginalDescription(description);
        setSuccess('Description saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save description');
      }
    } catch (err) {
      setError('Failed to save description');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDescription(originalDescription);
  };

  const hasChanges = description !== originalDescription;

  const insertMarkdown = (before: string, after: string = '') => {
    const textarea = document.querySelector('.editor-textarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = description.substring(start, end);
    const newText =
      description.substring(0, start) +
      before +
      selectedText +
      after +
      description.substring(end);

    setDescription(newText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  if (loading) {
    return (
      <div className="description-editor loading">
        <div className="loading-spinner" />
        <p>Loading description...</p>
      </div>
    );
  }

  return (
    <div className="description-editor">
      <div className="section-header">
        <h2>Property Description</h2>
        <p>Edit the property description using Markdown formatting</p>
      </div>

      {(error || success) && (
        <div className={`message ${error ? 'error' : 'success'}`}>
          {error || success}
        </div>
      )}

      <div className="editor-toolbar">
        <div className="toolbar-left">
          <button
            className="toolbar-button"
            onClick={() => insertMarkdown('# ')}
            title="Heading 1"
          >
            H1
          </button>
          <button
            className="toolbar-button"
            onClick={() => insertMarkdown('## ')}
            title="Heading 2"
          >
            H2
          </button>
          <button
            className="toolbar-button"
            onClick={() => insertMarkdown('**', '**')}
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            className="toolbar-button"
            onClick={() => insertMarkdown('*', '*')}
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            className="toolbar-button"
            onClick={() => insertMarkdown('- ')}
            title="Bullet List"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
          <button
            className="toolbar-button"
            onClick={() => insertMarkdown('[', '](url)')}
            title="Link"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
          </button>
          <div className="toolbar-divider" />
          <button
            className={`toolbar-button preview-toggle ${showPreview ? 'active' : ''}`}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>

        <div className="toolbar-right">
          {hasChanges && (
            <>
              <button
                className="toolbar-button reset"
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </button>
              <button
                className="save-button"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="button-spinner" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`editor-container ${showPreview ? 'preview-mode' : ''}`}>
        {!showPreview ? (
          <textarea
            className="editor-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter property description using Markdown..."
            spellCheck
          />
        ) : (
          <div className="editor-preview">
            <ReactMarkdown>{description}</ReactMarkdown>
          </div>
        )}
      </div>

      <div className="markdown-help">
        <h4>Markdown Quick Reference</h4>
        <div className="help-grid">
          <code># Heading 1</code>
          <code>## Heading 2</code>
          <code>**bold text**</code>
          <code>*italic text*</code>
          <code>- bullet item</code>
          <code>[link text](url)</code>
        </div>
      </div>
    </div>
  );
};

export default DescriptionEditor;
